import { ref, unref, computed } from 'vue';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';

export function useBulkActions() {
  const store = useStore();
  const { t } = useI18n();

  const selectedConversations = computed(() =>
    store.getters['bulkActions/getSelectedConversationIds']
  );

  const selectedInboxes = ref([]);
  const lastSelectedConversationId = ref(null);

  function selectConversation(conversationId, inboxId) {
    store.dispatch('bulkActions/setSelectedConversationIds', conversationId);
    selectedInboxes.value = [...selectedInboxes.value, inboxId];
    lastSelectedConversationId.value = conversationId;
  }

  function deSelectConversation(conversationId, inboxId) {
    store.dispatch('bulkActions/removeSelectedConversationIds', conversationId);
    selectedInboxes.value = selectedInboxes.value.filter(
      item => item !== inboxId
    );
    // Update lastSelected if we're deselecting the last selected conversation
    if (lastSelectedConversationId.value === conversationId) {
      lastSelectedConversationId.value = selectedConversations.value.length > 0
        ? selectedConversations.value[selectedConversations.value.length - 1]
        : null;
    }
  }

  function resetBulkActions() {
    store.dispatch('bulkActions/clearSelectedConversationIds');
    selectedInboxes.value = [];
    lastSelectedConversationId.value = null;
  }

  function selectAllConversations(check, conversationList) {
    const availableConversations = unref(conversationList);
    if (check) {
      store.dispatch(
        'bulkActions/setSelectedConversationIds',
        availableConversations.map(item => item.id)
      );
      selectedInboxes.value = availableConversations.map(item => item.inbox_id);
      // Update lastSelected to the last conversation in the list
      if (availableConversations.length > 0) {
        lastSelectedConversationId.value = availableConversations[availableConversations.length - 1].id;
      }
    } else {
      resetBulkActions();
    }
  }

  function selectRangeConversations(conversationId, conversationList) {
    const lastId = lastSelectedConversationId.value;

    // If no previous selection, just select the current one
    if (!lastId) {
      selectConversation(conversationId, conversationList[0]?.inbox_id);
      return;
    }

    const availableConversations = unref(conversationList);
    const lastIndex = availableConversations.findIndex(conv => conv.id === lastId);
    const currentIndex = availableConversations.findIndex(conv => conv.id === conversationId);

    // If either conversation is not found, return
    if (lastIndex === -1 || currentIndex === -1) {
      return;
    }

    // Determine the range to select
    const startIndex = Math.min(lastIndex, currentIndex);
    const endIndex = Math.max(lastIndex, currentIndex);

    // Select all conversations in the range at once for better performance
    const conversationsToSelect = availableConversations.slice(startIndex, endIndex + 1);
    const currentIds = new Set(selectedConversations.value);
    const idsToAdd = [];
    const inboxesToAdd = [];

    conversationsToSelect.forEach(conv => {
      if (!currentIds.has(conv.id)) {
        idsToAdd.push(conv.id);
        inboxesToAdd.push(conv.inbox_id);
      }
    });

    if (idsToAdd.length > 0) {
      // Add all IDs in a single dispatch for better performance
      store.dispatch('bulkActions/setSelectedConversationIds', idsToAdd);
      selectedInboxes.value = [...selectedInboxes.value, ...inboxesToAdd];
    }

    // Update lastSelected to the current conversation
    lastSelectedConversationId.value = conversationId;
  }

  function isConversationSelected(id) {
    return selectedConversations.value.includes(id);
  }

  // Same method used in context menu, conversationId being passed from there.
  async function onAssignAgent(agent, conversationId = null) {
    try {
      await store.dispatch('bulkActions/process', {
        type: 'Conversation',
        ids: conversationId || selectedConversations.value,
        fields: {
          assignee_id: agent.id,
        },
      });
      store.dispatch('bulkActions/clearSelectedConversationIds');
      if (conversationId) {
        useAlert(
          t('CONVERSATION.CARD_CONTEXT_MENU.API.AGENT_ASSIGNMENT.SUCCESFUL', {
            agentName: agent.name,
            conversationId,
          })
        );
      } else {
        useAlert(t('BULK_ACTION.ASSIGN_SUCCESFUL'));
      }
    } catch (err) {
      useAlert(t('BULK_ACTION.ASSIGN_FAILED'));
    }
  }

  // Same method used in context menu, conversationId being passed from there.
  async function onAssignLabels(newLabels, conversationId = null) {
    try {
      await store.dispatch('bulkActions/process', {
        type: 'Conversation',
        ids: conversationId || selectedConversations.value,
        labels: {
          add: newLabels,
        },
      });
      store.dispatch('bulkActions/clearSelectedConversationIds');
      if (conversationId) {
        useAlert(
          t('CONVERSATION.CARD_CONTEXT_MENU.API.LABEL_ASSIGNMENT.SUCCESFUL', {
            labelName: newLabels[0],
            conversationId,
          })
        );
      } else {
        useAlert(t('BULK_ACTION.LABELS.ASSIGN_SUCCESFUL'));
      }
    } catch (err) {
      useAlert(t('BULK_ACTION.LABELS.ASSIGN_FAILED'));
    }
  }

  // Remove labels from conversations (preserving atendimento-humano)
  async function onRemoveLabels(labelsToRemove, conversationId = null) {
    try {
      await store.dispatch('bulkActions/process', {
        type: 'Conversation',
        ids: conversationId || selectedConversations.value,
        labels: {
          remove: labelsToRemove,
        },
      });
      store.dispatch('bulkActions/clearSelectedConversationIds');
      if (conversationId) {
        useAlert(
          t('CONVERSATION.CARD_CONTEXT_MENU.API.LABEL_REMOVAL.SUCCESFUL', {
            conversationId,
          })
        );
      } else {
        useAlert(t('BULK_ACTION.LABELS.REMOVE_SUCCESFUL'));
      }
    } catch (err) {
      useAlert(t('BULK_ACTION.LABELS.REMOVE_FAILED'));
    }
  }

  async function onAssignTeamsForBulk(team) {
    try {
      await store.dispatch('bulkActions/process', {
        type: 'Conversation',
        ids: selectedConversations.value,
        fields: {
          team_id: team.id,
        },
      });
      store.dispatch('bulkActions/clearSelectedConversationIds');
      useAlert(t('BULK_ACTION.TEAMS.ASSIGN_SUCCESFUL'));
    } catch (err) {
      useAlert(t('BULK_ACTION.TEAMS.ASSIGN_FAILED'));
    }
  }

  async function onUpdateConversations(status, snoozedUntil) {
    try {
      await store.dispatch('bulkActions/process', {
        type: 'Conversation',
        ids: selectedConversations.value,
        fields: {
          status,
        },
        snoozed_until: snoozedUntil,
      });
      store.dispatch('bulkActions/clearSelectedConversationIds');
      useAlert(t('BULK_ACTION.UPDATE.UPDATE_SUCCESFUL'));
    } catch (err) {
      useAlert(t('BULK_ACTION.UPDATE.UPDATE_FAILED'));
    }
  }

  async function onUpdatePriority(priority) {
    try {
      await store.dispatch('bulkActions/process', {
        type: 'Conversation',
        ids: selectedConversations.value,
        fields: {
          priority,
        },
      });
      store.dispatch('bulkActions/clearSelectedConversationIds');
      useAlert(t('BULK_ACTION.PRIORITY.ASSIGN_SUCCESFUL'));
    } catch (err) {
      useAlert(t('BULK_ACTION.PRIORITY.ASSIGN_FAILED'));
    }
  }

  return {
    selectedConversations,
    selectedInboxes,
    selectConversation,
    deSelectConversation,
    selectAllConversations,
    selectRangeConversations,
    resetBulkActions,
    isConversationSelected,
    onAssignAgent,
    onAssignLabels,
    onRemoveLabels,
    onAssignTeamsForBulk,
    onUpdateConversations,
    onUpdatePriority,
  };
}
